// Helpers
import { getFormatUTCDate } from "@/Common/helpers/Dates";
import { getCellClassNameFn, isFooterCell } from "@/Common/helpers/Tables";
// Custom components
import TableLoader from "@/Layouts/components/Skeletons/TableLoader";
import { InformationTable } from "../../InformationTable";
import { TypographyCell } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
// Materials
import { GridColDef } from "@mui/x-data-grid-premium";
// Types and constants
import { Issue } from "@/Common/types/tables";
import { CommonSalesTableProps } from "@/Washouts/types/commons";
import { HEADER_CLASS_NAME, ID_GRID_COLDEF } from "@/Washouts/constants";
import { DATE_FORMAT } from "@/Constants/date";

function getInitialColumns(reference: string, issues: Issue[]): GridColDef[] {
	return [
		ID_GRID_COLDEF,
		{
			field: "date",
			headerName: "Date",
			width: 100,
			type: "date",
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}, reference),
			valueFormatter: (value) => getFormatUTCDate(value, DATE_FORMAT.usDateDashShortYear),
			renderCell: (params) => (
				<TypographyCell reference={reference} params={params} issues={issues}>
					{isFooterCell(params) ? "Spiffs Total" : null}
				</TypographyCell>
			),
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "spiffName",
			headerName: "Spiff Name",
			width: 130,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}, reference),
			renderCell: (params) => <TypographyCell reference={reference} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "spiffType",
			headerName: "Spiff Type",
			width: 130,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}, reference),
			renderCell: (params) => <TypographyCell reference={reference} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "stocksNumbers",
			headerName: "Stocks #",
			width: 199,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}, reference),
			renderCell: (params) => <TypographyCell reference={reference} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
			valueFormatter: (value?: unknown) => {
				if (value instanceof Array) {
					return value.join(", ") || "";
				}
				return "";
			},
		},
		{
			field: "amount",
			headerName: "Amount",
			width: 100,
			type: "number",
			align: "right",
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, { isMoney: true }, {}, reference),
			renderCell: (params) => (
				<TypographyCell render="absolute" reference={reference} params={params} issues={issues} hints={{ isMoney: true }} />
			),
			renderHeader: (params) => <TypographyHeader params={params} right />,
		},
	];
}

const negatives = ["chargebackInvoiceAmount", "saleGross", "unit", "chargebackAmount", "chargebackCommission"];

export default function SpiffsTable(props: Readonly<CommonSalesTableProps>) {
	if (props.loading) {
		return <TableLoader smallTitle content sx={{ padding: "15px 10px 0px 10px" }} />;
	}

	return (
		<>{props.dataPresent && <InformationTable {...props} title="Spiffs" colDefFn={getInitialColumns} negativeColumns={negatives} />}</>
	);
}
